import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ColorPicker from "rsuite-color-picker";
import {
  useAccountStatusCodeColorsQuery,
  useResetAccountStatusCodeColorMutation,
  useUpdateAccountStatusCodeColorMutation,
} from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { TAccountStatusCodeColor } from "../../../api/types/GenericStatusCode";
import svgIcons from "../../../assets/svg/svgIcons";
import HeaderView from "../../../components/admin/headerView";
import StatusBadge from "../../../components/statusBadge";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import {
  COLORBLIND_SAFE_COLORS,
  FT1_DROPDOWN_COLORS,
  MIN_STATUS_CONTRAST,
  contrastRatio,
  readableTextOn,
  toCssColor,
} from "../../../utils/statusColor";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import { RsuitePickedColor } from "../heatmap/components/types";

type ColorDraft = { foregroundColor: string; backgroundColor: string };

const toPickerHex = (value: string) => {
  const css = toCssColor(value);
  return css && css.startsWith("#") ? css : "";
};

const SwatchRow: FC<{
  label: string;
  colors: { label: string; value: string }[];
  onPick: (value: string) => void;
  disabled: boolean;
}> = ({ label, colors, onPick, disabled }) => (
  <div className="flex flex-wrap items-center gap-1">
    <span className="text-[11px] text-heading-gray666 w-28 shrink-0">{label}</span>
    {colors.map((color) => (
      <button
        key={`${label}-${color.value}`}
        type="button"
        title={`${color.label} ${color.value}`}
        aria-label={`${color.label} ${color.value}`}
        disabled={disabled}
        onClick={() => onPick(color.value)}
        className="h-5 w-5 rounded border border-gray-300 disabled:opacity-50"
        style={{ backgroundColor: color.value }}
      />
    ))}
  </div>
);

const ColorField: FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}> = ({ label, value, onChange, disabled }) => {
  const { t } = useTranslation("translation", { keyPrefix: "admins.eventColors" });
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <label className="text-sm font-display font-semibold text-field-label-valid w-28">{label}</label>
        <div className="flex h-9">
          <ColorPicker
            value={toPickerHex(value) || "#FFFFFF"}
            onChange={(picked: RsuitePickedColor) => onChange(picked.hex.toUpperCase())}
            disabled={disabled}
          />
        </div>
        <span className="text-xs font-mono w-20">{value || t("default_colour")}</span>
        <button
          type="button"
          className="text-xs text-accent-blue-bright disabled:opacity-50"
          disabled={disabled || !value}
          onClick={() => onChange("")}
        >
          {t("clear")}
        </button>
      </div>
      <SwatchRow label={t("presets_ft1")} colors={FT1_DROPDOWN_COLORS} onPick={onChange} disabled={disabled} />
      <SwatchRow
        label={t("presets_colorblind")}
        colors={COLORBLIND_SAFE_COLORS}
        onPick={onChange}
        disabled={disabled}
      />
    </div>
  );
};

const EventColorEditor: FC<{
  row: TAccountStatusCodeColor;
  userCanEdit: boolean;
  onClose: () => void;
}> = ({ row, userCanEdit, onClose }) => {
  const { t } = useTranslation("translation", { keyPrefix: "admins.eventColors" });
  const { t: tMain } = useTranslation();
  const [draft, setDraft] = useState<ColorDraft>({
    foregroundColor: toPickerHex(row.foregroundColor),
    backgroundColor: toPickerHex(row.backgroundColor),
  });
  const [updateColor, { isLoading: isSaving }] = useUpdateAccountStatusCodeColorMutation();
  const [resetColor, { isLoading: isResetting }] = useResetAccountStatusCodeColorMutation();

  // What the badge will actually show: a missing text colour is picked for readability.
  const shownText = draft.foregroundColor || (draft.backgroundColor ? readableTextOn(draft.backgroundColor) : "");
  const ratio = shownText && draft.backgroundColor ? contrastRatio(shownText, draft.backgroundColor) : undefined;
  const lowContrast = ratio !== undefined && ratio < MIN_STATUS_CONTRAST;
  const busy = isSaving || isResetting;

  const handleError = (error: any, fallback: string) => {
    const messages = error?.data?.error
      ? typeof error.data.error === "string"
        ? [error.data.error]
        : Object.values(error.data.error as Record<string, string>)
      : error?.data
        ? serializeErrorKeyValues(error.data)
        : [fallback];
    toast.error(messages.join(" "));
  };

  const handleSave = () => {
    updateColor({ statusCode: row.statusCode, ...draft })
      .unwrap()
      .then(() => {
        toast.success(t("toast.saved"));
        onClose();
      })
      .catch((error) => handleError(error, t("toast.save_failed")));
  };

  const handleReset = () => {
    resetColor(row.statusCode)
      .unwrap()
      .then(() => {
        toast.success(t("toast.reset"));
        onClose();
      })
      .catch((error) => handleError(error, t("toast.save_failed")));
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-white rounded-md border border-gray-200">
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold">{t("preview")}</span>
        <StatusBadge
          label={row.description || row.statusName}
          foregroundColor={draft.foregroundColor}
          backgroundColor={draft.backgroundColor}
          className="text-xs font-medium"
        />
        {ratio !== undefined && (
          <span className={`text-xs ${lowContrast ? "text-red-600 font-semibold" : "text-heading-gray666"}`}>
            {t("contrast", { ratio: ratio.toFixed(1) })}
          </span>
        )}
      </div>
      {lowContrast && (
        <p role="alert" className="text-xs text-red-600">
          {t("low_contrast_warning", { min: MIN_STATUS_CONTRAST })}
        </p>
      )}
      <ColorField
        label={t("text_colour")}
        value={draft.foregroundColor}
        onChange={(value) => setDraft((prev) => ({ ...prev, foregroundColor: value }))}
        disabled={!userCanEdit || busy}
      />
      <ColorField
        label={t("background_colour")}
        value={draft.backgroundColor}
        onChange={(value) => setDraft((prev) => ({ ...prev, backgroundColor: value }))}
        disabled={!userCanEdit || busy}
      />
      <div className="flex gap-2 justify-end">
        {row.isOverridden && (
          <button
            type="button"
            className="h-9 px-4 rounded-md border border-gray-300 text-sm disabled:opacity-50"
            disabled={!userCanEdit || busy}
            onClick={handleReset}
          >
            {t("reset_to_default")}
          </button>
        )}
        <button
          type="button"
          className="h-9 px-4 rounded-md border border-gray-300 text-sm"
          onClick={onClose}
          disabled={busy}
        >
          {tMain("cancel")}
        </button>
        <button
          type="button"
          className="h-9 px-4 rounded-md bg-accent-blue-bright text-white text-sm disabled:opacity-50"
          disabled={!userCanEdit || busy}
          onClick={handleSave}
        >
          {tMain("save")}
        </button>
      </div>
    </div>
  );
};

const ScreenAdminEventColors: React.FC = () => {
  const { t } = useTranslation("translation", { keyPrefix: "admins.eventColors" });
  const { getUserACLAccessLevel } = useACL();
  const userCanEdit =
    getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_STATUS_CODE) >= ROLE_ACCESS_LEVELS.WRITE;

  const { data, isFetching, isError } = useAccountStatusCodeColorsQuery();
  const [search, setSearch] = useState("");
  const [onlyCustom, setOnlyCustom] = useState(false);
  const [editingCode, setEditingCode] = useState<number | null>(null);

  useEffect(() => {
    if (isError) toast.error(t("toast.load_failed"));
  }, [isError, t]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data || []).filter(
      (row) =>
        (!onlyCustom || row.isOverridden) &&
        (!term ||
          `${row.statusCode}`.includes(term) ||
          row.description.toLowerCase().includes(term) ||
          row.statusName.toLowerCase().includes(term))
    );
  }, [data, search, onlyCustom]);

  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2 pt-4 flex flex-col gap-3`}>
        <p className="text-sm text-heading-gray666">{t("sub_heading")}</p>
        <div className="flex items-center gap-4">
          <input
            type="search"
            className="h-9 w-72 rounded-md border border-gray-300 px-3 text-sm"
            placeholder={t("search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={onlyCustom} onChange={(e) => setOnlyCustom(e.target.checked)} />
            {t("only_custom")}
          </label>
          <div className="flex-grow" />
          {isFetching && <img src={svgIcons.LoadingAnimation} alt="loading-animation" className="w-8 h-8" />}
        </div>
        <div className={`rounded-lg bg-accent-blue-pale overflow-x-auto ${isFetching ? "opacity-40" : ""}`}>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-heading-gray666">
                <th className="px-4 py-2">{t("col_code")}</th>
                <th className="px-4 py-2">{t("col_status")}</th>
                <th className="px-4 py-2">{t("col_preview")}</th>
                <th className="px-4 py-2">{t("col_source")}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <React.Fragment key={row.statusCode}>
                  <tr className="border-t border-gray-200 bg-white">
                    <td className="px-4 py-2 font-mono text-xs">{row.statusCode}</td>
                    <td className="px-4 py-2">{row.description || row.statusName}</td>
                    <td className="px-4 py-2">
                      <StatusBadge
                        label={row.description || row.statusName}
                        foregroundColor={row.foregroundColor}
                        backgroundColor={row.backgroundColor}
                        className="text-xs font-medium"
                      />
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {row.isOverridden ? t("source_account") : t("source_default")}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        className="text-accent-blue-bright text-sm font-medium"
                        onClick={() => setEditingCode(editingCode === row.statusCode ? null : row.statusCode)}
                      >
                        {userCanEdit ? t("edit") : t("view")}
                      </button>
                    </td>
                  </tr>
                  {editingCode === row.statusCode && (
                    <tr>
                      <td colSpan={5} className="px-4 py-2">
                        <EventColorEditor row={row} userCanEdit={userCanEdit} onClose={() => setEditingCode(null)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {!isFetching && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-heading-gray666">
                    {t("no_results")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ScreenAdminEventColors;
